import { useState } from "react";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  ImageColumn,
  type ImagePanelState,
} from "@/features/image/ImageColumn";
import { PromptBar } from "@/features/shared/PromptBar";
import { SectionRail } from "@/features/shared/SectionRail";
import { apiJson } from "@/lib/api";
import {
  defaultImageModels,
  falModelOptions,
  MAX_IMAGE_PANELS,
  type ImageGenerationResult,
} from "@/lib/playground";

const newPanel = (model: string): ImagePanelState => ({
  id: crypto.randomUUID(),
  model,
  status: "idle",
  result: null,
  error: null,
});

export const ImageArenaPage = () => {
  const [panels, setPanels] = useState<ImagePanelState[]>(
    defaultImageModels.map(newPanel)
  );

  const updatePanel = (
    id: string,
    update: (panel: ImagePanelState) => ImagePanelState
  ) => {
    setPanels((current) =>
      current.map((panel) => (panel.id === id ? update(panel) : panel))
    );
  };

  const runPanel = async (id: string, model: string, prompt: string) => {
    try {
      const result = await apiJson<ImageGenerationResult>("/api/image", {
        method: "POST",
        body: JSON.stringify({ model, prompt }),
      });
      updatePanel(id, (panel) => ({ ...panel, status: "done", result }));
    } catch (error) {
      updatePanel(id, (panel) => ({
        ...panel,
        status: "error",
        error:
          error instanceof Error ? error.message : "Image generation failed.",
      }));
    }
  };

  const send = (prompt: string) => {
    const targets = panels.map((panel) => ({
      id: panel.id,
      model: panel.model.trim(),
    }));

    setPanels((current) =>
      current.map((panel) => ({
        ...panel,
        status: "loading" as const,
        result: null,
        error: null,
      }))
    );

    for (const target of targets) {
      void runPanel(target.id, target.model, prompt);
    }
  };

  const addPanel = () => {
    if (panels.length >= MAX_IMAGE_PANELS) return;
    const used = new Set(panels.map((panel) => panel.model));
    const nextModel =
      falModelOptions.find((option) => !used.has(option.id))?.id ??
      falModelOptions[0].id;
    setPanels((current) => [...current, newPanel(nextModel)]);
  };

  const busy = panels.some((panel) => panel.status === "loading");

  return (
    <div className="space-y-4">
      <SectionRail
        index="[02]"
        kicker="Image arena"
        meta={`fal.ai · max ${MAX_IMAGE_PANELS} panels`}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          One prompt, up to {MAX_IMAGE_PANELS} image models via fal.ai. Pick a
          suggested model or type any fal-ai endpoint id.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={addPanel}
          disabled={busy || panels.length >= MAX_IMAGE_PANELS}
        >
          <Plus className="h-4 w-4" />
          Add model ({panels.length}/{MAX_IMAGE_PANELS})
        </Button>
      </div>

      <PromptBar
        onSend={send}
        busy={busy}
        placeholder="Describe the image to generate with every model…"
        sendLabel="Generate"
      />

      <div className="grid border-l border-t md:grid-cols-2 xl:grid-cols-3">
        {panels.map((panel) => (
          <ImageColumn
            key={panel.id}
            panel={panel}
            onModelChange={(model) =>
              updatePanel(panel.id, (current) => ({ ...current, model }))
            }
            onRemove={() =>
              setPanels((current) =>
                current.filter((candidate) => candidate.id !== panel.id)
              )
            }
            canRemove={panels.length > 1}
          />
        ))}
      </div>
    </div>
  );
};
